/** @fileoverview Hooks that read checklist or timeline rows from the shared rows store and ask it to reload. */

import { useCallback, useMemo, useSyncExternalStore } from 'react';

import { stableStringify } from '../../lib/utils';
import type {
  FabrikRecordKeyRefValue,
  FabrikStateComponent,
  InputSnapshot,
} from '../../types/apps';

import { EMPTY_ENTRY, rowsKeyOf, useComponentStateStore } from './componentStateStore';
import { fieldsForRecordKey } from './helpers';
import type { FieldValues } from './types';

/** Subscribe function used when there is no store, so the hook order stays the same. */
const subscribeToNothing = () => () => undefined;

/**
 * Reads the rows of one checklist or timeline for the record on screen.
 * Blocks that show the same rows share one request. Rows from the last load stay visible while a
 * reload runs, so the screen does not flicker.
 * @param componentId id of the checklist or timeline
 * @param fields record fields of the screen
 * @param recordKey fields that identify this component's record
 */
export const useComponentStateRows = (
  componentId: string,
  fields: FieldValues,
  recordKey: FabrikRecordKeyRefValue[],
  screenId: string,
  inputs: InputSnapshot
) => {
  const { store } = useComponentStateStore();

  /* Keep only key fields so unrelated screen data does not trigger or enter this request. */
  const recordFields = useMemo(() => fieldsForRecordKey(fields, recordKey), [fields, recordKey]);
  /*
   * Stable text forms so a new object with the same key values, or a definition edit that keeps
   * the same record key, does not load again.
   */
  const fieldsKey = useMemo(() => stableStringify(recordFields), [recordFields]);
  const stableFields = useMemo(() => JSON.parse(fieldsKey) as FieldValues, [fieldsKey]);
  const inputsKey = useMemo(() => stableStringify(inputs), [inputs]);
  const stableInputs = useMemo(() => JSON.parse(inputsKey) as InputSnapshot, [inputsKey]);
  const recordKeyText = useMemo(() => stableStringify(recordKey), [recordKey]);
  const stableRecordKey = useMemo(
    () => JSON.parse(recordKeyText) as FabrikRecordKeyRefValue[],
    [recordKeyText]
  );
  const key = rowsKeyOf(componentId, stableRecordKey, stableFields, screenId, stableInputs);

  const subscribe = useCallback(
    (listener: () => void) =>
      store
        ? store.subscribe({
            componentId,
            screenId,
            fields: stableFields,
            inputs: stableInputs,
            recordKey: stableRecordKey,
            listener,
          })
        : subscribeToNothing(),
    [componentId, screenId, stableFields, stableInputs, stableRecordKey, store]
  );
  const getSnapshot = useCallback(() => (store ? store.getEntry(key) : EMPTY_ENTRY), [key, store]);
  const { rows, hasLoaded, loading, error } = useSyncExternalStore(subscribe, getSnapshot);

  return {
    /** False in the builder when the app is not saved yet. */
    isAvailable: Boolean(store),
    /** True until the first answer arrives and during every reload; old rows stay in `rows` meanwhile. */
    isLoading: Boolean(store) && (!hasLoaded || loading),
    rows,
    error,
  };
};

/** Returns functions that reload the rows changed by a tick or by an action run. */
type ComponentStateTarget = Pick<FabrikStateComponent, 'id' | 'recordKey'>;

export const useComponentStateRefresh = () => {
  const { store } = useComponentStateStore();

  /**
   * Reloads the rows of the given components for one record; settles when the reload is done.
   * @param componentIds ids of the checklists or timelines to reload
   * @param fields record fields of the screen
   */
  const refreshComponents = useCallback(
    (
      components: ComponentStateTarget[],
      fields: FieldValues,
      screenId: string,
      inputs: InputSnapshot
    ): Promise<void> =>
      store
        ? store.invalidate(
            components.map(({ id, recordKey }) =>
              rowsKeyOf(id, recordKey, fieldsForRecordKey(fields, recordKey), screenId, inputs)
            )
          )
        : Promise.resolve(),
    [store]
  );

  /**
   * Reloads the rows of every component the action writes to; settles when the reload is done.
   * @param actionId id of the action that just ran
   * @param fields record fields of the screen
   */
  return { refreshComponents, refreshAfterAction: refreshComponents };
};
